import axios from "axios";
import { BASE_API_URL } from "../constants/dropdown";

const LOAN_BASE = `${BASE_API_URL}/admin/loan`;

// Isolated instance — does NOT inherit the global 401→logout interceptor,
// so a permission error on loan endpoints never logs the user out.
const loanAxios = axios.create();

// Stats
export const GET_LOAN_METRICS_REQUEST = "GET_LOAN_METRICS_REQUEST";
export const GET_LOAN_METRICS_SUCCESS = "GET_LOAN_METRICS_SUCCESS";
export const GET_LOAN_METRICS_FAIL = "GET_LOAN_METRICS_FAIL";

// Applications list
export const GET_LOAN_APPLICATIONS_REQUEST = "GET_LOAN_APPLICATIONS_REQUEST";
export const GET_LOAN_APPLICATIONS_SUCCESS = "GET_LOAN_APPLICATIONS_SUCCESS";
export const GET_LOAN_APPLICATIONS_FAIL = "GET_LOAN_APPLICATIONS_FAIL";

// Detail (includes schedule, contacts, guarantors, documents)
export const GET_LOAN_DETAIL_REQUEST = "GET_LOAN_DETAIL_REQUEST";
export const GET_LOAN_DETAIL_SUCCESS = "GET_LOAN_DETAIL_SUCCESS";
export const GET_LOAN_DETAIL_FAIL = "GET_LOAN_DETAIL_FAIL";

// Decision step 1 — request OTP (reuses REVIEW_LOAN reducer)
export const DECISION_OTP_REQUEST = "REVIEW_LOAN_REQUEST";
export const DECISION_OTP_SUCCESS = "REVIEW_LOAN_SUCCESS";
export const DECISION_OTP_FAIL = "REVIEW_LOAN_FAIL";
export const DECISION_OTP_RESET = "REVIEW_LOAN_RESET";

// Decision step 2 — submit (reuses APPROVE_LOAN reducer)
export const DECISION_SUBMIT_REQUEST = "APPROVE_LOAN_REQUEST";
export const DECISION_SUBMIT_SUCCESS = "APPROVE_LOAN_SUCCESS";
export const DECISION_SUBMIT_FAIL = "APPROVE_LOAN_FAIL";
export const DECISION_SUBMIT_RESET = "APPROVE_LOAN_RESET";

const getConfig = (getState) => {
  const { admin2faLogin: { userInfo } } = getState();
  return {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${userInfo?.token?.access_token}`,
    },
  };
};

const buildQuery = (params) => {
  const valid = Object.entries(params).filter(
    ([, v]) => v !== "" && v !== null && v !== undefined,
  );
  if (valid.length === 0) return "";
  return "?" + valid.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
};

const errMsg = (error) =>
  error.response?.data?.message || error.message;

// GET /stats
export const fetchLoanStats = () => async (dispatch, getState) => {
  try {
    dispatch({ type: GET_LOAN_METRICS_REQUEST });
    const { data } = await loanAxios.get(`${LOAN_BASE}/stats`, getConfig(getState));
    dispatch({ type: GET_LOAN_METRICS_SUCCESS, payload: data });
  } catch (error) {
    dispatch({ type: GET_LOAN_METRICS_FAIL, payload: errMsg(error) });
  }
};

// GET /requests — queue
export const fetchLoanApplications =
  (page = 1, size = 50, fromdate = "", todate = "", statusid = "", loan_mode = "") =>
  async (dispatch, getState) => {
    try {
      dispatch({ type: GET_LOAN_APPLICATIONS_REQUEST });
      const query = buildQuery({ page, size, fromdate, todate, statusid, loan_mode });
      const { data } = await loanAxios.get(
        `${LOAN_BASE}/requests${query}`,
        getConfig(getState),
      );
      dispatch({ type: GET_LOAN_APPLICATIONS_SUCCESS, payload: data });
    } catch (error) {
      dispatch({ type: GET_LOAN_APPLICATIONS_FAIL, payload: errMsg(error) });
    }
  };

// GET /requests/{id} — full detail (side-effect: moves Submitted/PendingReview → UnderReview)
export const fetchLoanDetail = (id) => async (dispatch, getState) => {
  try {
    dispatch({ type: GET_LOAN_DETAIL_REQUEST });
    const { data } = await loanAxios.get(
      `${LOAN_BASE}/requests/${id}`,
      getConfig(getState),
    );
    dispatch({ type: GET_LOAN_DETAIL_SUCCESS, payload: data });
  } catch (error) {
    dispatch({ type: GET_LOAN_DETAIL_FAIL, payload: errMsg(error) });
  }
};

// GET /drafts
export const fetchLoanDrafts =
  (page = 1, size = 50) =>
  async (dispatch, getState) => {
    try {
      dispatch({ type: GET_LOAN_APPLICATIONS_REQUEST });
      const query = buildQuery({ page, size });
      const { data } = await loanAxios.get(
        `${LOAN_BASE}/drafts${query}`,
        getConfig(getState),
      );
      dispatch({ type: GET_LOAN_APPLICATIONS_SUCCESS, payload: data });
    } catch (error) {
      dispatch({ type: GET_LOAN_APPLICATIONS_FAIL, payload: errMsg(error) });
    }
  };

// POST /requests/{id}/decision/otp — step 1: send OTP to admin's email
export const requestDecisionOtp = (id) => async (dispatch, getState) => {
  try {
    dispatch({ type: DECISION_OTP_REQUEST });
    const { data } = await loanAxios.post(
      `${LOAN_BASE}/requests/${id}/decision/otp`,
      {},
      getConfig(getState),
    );
    dispatch({ type: DECISION_OTP_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const msg = errMsg(error);
    dispatch({ type: DECISION_OTP_FAIL, payload: msg });
    throw new Error(msg);
  }
};

// POST /requests/{id}/decision — step 2: submit decision with OTP
// body: { decision: "approved"|"rejected"|"awaiting_info", otp, approved_amount?, approved_tenor?, reject_reason? }
export const submitDecision = (id, body) => async (dispatch, getState) => {
  try {
    dispatch({ type: DECISION_SUBMIT_REQUEST });
    const { data } = await loanAxios.post(
      `${LOAN_BASE}/requests/${id}/decision`,
      body,
      getConfig(getState),
    );
    dispatch({ type: DECISION_SUBMIT_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const msg = errMsg(error);
    dispatch({ type: DECISION_SUBMIT_FAIL, payload: msg });
    throw new Error(msg);
  }
};
